import { useTranslation } from "react-i18next"

export type MembershipFormTranslations = typeof defaultTranslations

const defaultTranslations = {
  pageHeader: "Let's add a new member",
  firstnameLabel: "Firstname",
  lastnameLabel: "Lastname",
  emailLabel: "Email",
  firstnameRequiredText: "Firstname is required",
  validEmailRequiredText: "Email must be a valid email",
  membershipTypeLabel: "Membership type",
  normalMembershipLabel: "Normal",
  limitedMembershipLabel: "Limited",
  fillBillingInfoText: "Please fill in the billing information before sending the invitation!!!",
  clickMeLabel: "Click me",
  sendInvitationLabel: "Invite user and allow access to this organisation",
  normalMembershipHelperText:
    "A normal member has access to all projects, tasks, to-dos, resources, and other information within the organisation. However, specific actions can be restricted by adjusting the member's permissions.",
  limitedMembershipHelperText:
    "A limited member can access their own projects, tasks, to-dos, and other relevant information within the organisation. Additionally, they will be able to view resources specified in the input field of this form. Permissions for a limited member operate in the same way as those for a regular member.",
  addMemberButtonLabel: "Add member",
  addingMemberButtonLabel: "Adding member...",
  addMemberFailedMessage: "Something went wrong! Please try again later",
  addMemberSuccessMessage: "Member added. New member can be viewed in the members page.",
  duplicateEmailError: "There is an existing member with this email. Please try with another email",
  choosePermissionsLabel: "Choose what access to give to {{firstname}}",
  standardMemberLabel: "Standard member",
  limitedMemberLabel: "Limited member",
  copyFromMemberLabel: "Copied member",
  copyingMemberLabel: "Copying {{name}}",
  copiedMemberHelperText: "{{copy_to_name}} will have exact same permissions as {{copy_from_name}}",
  customizeAssociationsLabel: "Customize associations",
  customizePermissionsLabel: "Customize permissions",
  chooseMemberToCopyLabel: "Choose a member to copy from",
  searchMembersPlaceholder: "Search for members",
  resetButtonLabel: "Reset",
  invitationInfo:
    "We will send an email to {{email}} with a link to join the organisation. The user will be able to access the organisation after accepting the invitation.",
  noInvitationInfo: "User will not be invited to the organisation and will not have access to the organisation",
  viewMembersButtonLabel: "View members",
  invitationLanguageDescription: "The invitation email will be sent in the selected language",
  note: "Note",
  chooseInvitationLanguage: "Choose invitation language",
  provideValidEmailToInvite: "Email is required to accept the invitation. Please provide a valid email.",
  member: "Member",
  customizeAccess: "Customize access level",
  accessLevelInfo:
    "Upon accepting the invitation, {{member}} will have {{accessLevel}} access. You can customize the access level before sending the invitation.",
  freePlanLimitHeader: "Free plan limit reached",
  freePlanLimitDescription1: "Starbrix is free for upto 3 members.",
  freePlanLimitDescription2: "To invite a 4th member please enter your billing information.",
  enterBillingInfo: "Enter billing information",
}

type UseTranslationsParams = {
  copyingMemberName?: string
  copyToName?: string
  copyFromName?: string
  accessLevelMember?: string
  invitationEmail?: string
}

export const useTranslations = ({
  copyingMemberName = "",
  copyToName = "",
  copyFromName = "",
  accessLevelMember,
  invitationEmail = "",
}: UseTranslationsParams = {}): MembershipFormTranslations => {
  const { t } = useTranslation()
  const { t: tMembership } = useTranslation("membership")
  // Do not remove this line. This is required to load the permission translations used by AbilitiesTable component,
  // If we do not load the permission translations here, the permission translations will be fetched once the AbilitiesTable component is rendered,
  // And fetching translations in the middle will cause flickering of the UI
  useTranslation("permission") // <-- Do not remove this line

  const member = tMembership("member", { defaultValue: defaultTranslations.member })
  const standardMemberLabel = tMembership("standardMemberLabel", {
    defaultValue: defaultTranslations.standardMemberLabel,
  })

  return {
    pageHeader: tMembership("newMemberPage.pageHeader", { defaultValue: defaultTranslations.pageHeader }),
    firstnameLabel: tMembership("membershipSettingsView.firstnameLabel", {
      defaultValue: defaultTranslations.firstnameLabel,
    }),
    lastnameLabel: tMembership("membershipSettingsView.lastnameLabel", {
      defaultValue: defaultTranslations.lastnameLabel,
    }),
    emailLabel: tMembership("membershipSettingsView.emailLabel", { defaultValue: defaultTranslations.emailLabel }),
    firstnameRequiredText: defaultTranslations.firstnameRequiredText,
    validEmailRequiredText: tMembership("newMemberPage.validEmailRequiredText", {
      defaultValue: defaultTranslations.validEmailRequiredText,
    }),
    fillBillingInfoText: tMembership("newMemberPage.fillBillingInfoText", {
      defaultValue: defaultTranslations.fillBillingInfoText,
    }),
    clickMeLabel: tMembership("newMemberPage.clickMeLabel", { defaultValue: defaultTranslations.clickMeLabel }),
    sendInvitationLabel: tMembership("newMemberPage.sendInvitationLabel", {
      defaultValue: defaultTranslations.sendInvitationLabel,
    }),
    membershipTypeLabel: tMembership("membershipSettingsView.membershipTypeLabel", {
      defaultValue: defaultTranslations.membershipTypeLabel,
    }),
    normalMembershipLabel: tMembership("membershipSettingsView.normalMembershipLabel", {
      defaultValue: defaultTranslations.normalMembershipLabel,
    }),
    normalMembershipHelperText: tMembership("newMemberPage.normalMembershipHelperText", {
      defaultValue: defaultTranslations.normalMembershipHelperText,
    }),
    limitedMembershipLabel: tMembership("membershipSettingsView.limitedMembershipLabel", {
      defaultValue: defaultTranslations.limitedMembershipLabel,
    }),
    limitedMembershipHelperText: tMembership("newMemberPage.limitedMembershipHelperText", {
      defaultValue: defaultTranslations.limitedMembershipHelperText,
    }),
    addMemberButtonLabel: tMembership("newMemberPage.addMemberButtonLabel", {
      defaultValue: defaultTranslations.addMemberButtonLabel,
    }),
    addingMemberButtonLabel: tMembership("newMemberPage.addingMemberButtonLabel", {
      defaultValue: defaultTranslations.addingMemberButtonLabel,
    }),
    addMemberFailedMessage: tMembership("newMemberPage.addMemberFailedMessage", {
      defaultValue: defaultTranslations.addMemberFailedMessage,
    }),
    addMemberSuccessMessage: tMembership("newMemberPage.addMemberSuccessMessage", {
      defaultValue: defaultTranslations.addMemberSuccessMessage,
    }),
    duplicateEmailError: tMembership("newMemberPage.duplicateEmailError", {
      defaultValue: defaultTranslations.duplicateEmailError,
    }),
    resetButtonLabel: tMembership("newMemberPage.resetButtonLabel", {
      defaultValue: defaultTranslations.resetButtonLabel,
    }),
    standardMemberLabel,
    limitedMemberLabel: tMembership("limitedMemberLabel", { defaultValue: defaultTranslations.limitedMemberLabel }),
    copyFromMemberLabel: tMembership("copyFromMemberLabel", {
      defaultValue: defaultTranslations.copyFromMemberLabel,
    }),
    copyingMemberLabel: tMembership("copyingMemberLabel", {
      defaultValue: defaultTranslations.copyingMemberLabel,
      name: copyingMemberName,
    }),
    copiedMemberHelperText: tMembership("copiedMemberHelperText", {
      defaultValue: defaultTranslations.copiedMemberHelperText,
      copy_to_name: copyToName || member,
      copy_from_name: copyFromName,
    }),
    customizeAssociationsLabel: tMembership("customizeAssociationsLabel", {
      defaultValue: defaultTranslations.customizeAssociationsLabel,
    }),
    customizePermissionsLabel: tMembership("customizePermissionsLabel", {
      defaultValue: defaultTranslations.customizePermissionsLabel,
    }),
    chooseMemberToCopyLabel: tMembership("chooseMemberToCopyLabel", {
      defaultValue: defaultTranslations.chooseMemberToCopyLabel,
    }),
    searchMembersPlaceholder: tMembership("searchMembersPlaceholder", {
      defaultValue: defaultTranslations.searchMembersPlaceholder,
    }),
    choosePermissionsLabel: tMembership("choosePermissionsLabel", {
      defaultValue: defaultTranslations.choosePermissionsLabel,
    }),
    invitationInfo: tMembership("invitationInfo", {
      defaultValue: defaultTranslations.invitationInfo,
      email: invitationEmail,
    }),
    noInvitationInfo: tMembership("noInvitationInfo", { defaultValue: defaultTranslations.noInvitationInfo }),
    viewMembersButtonLabel: tMembership("newMemberPage.viewMembersButtonLabel", {
      defaultValue: defaultTranslations.viewMembersButtonLabel,
    }),
    invitationLanguageDescription: tMembership("invitationLanguageDescription", {
      defaultValue: defaultTranslations.invitationLanguageDescription,
    }),
    note: t("note", { defaultValue: defaultTranslations.note }),
    chooseInvitationLanguage: defaultTranslations.chooseInvitationLanguage,
    provideValidEmailToInvite: tMembership("provideValidEmailToInvite", {
      defaultValue: defaultTranslations.provideValidEmailToInvite,
    }),
    accessLevelInfo: tMembership("accessLevelInfo", {
      defaultValue: defaultTranslations.accessLevelInfo,
      member: accessLevelMember || member.toLowerCase(),
      accessLevel: standardMemberLabel.toLowerCase(),
    }),
    member,
    customizeAccess: tMembership("customizeAccess", { defaultValue: defaultTranslations.customizeAccess }),
    freePlanLimitHeader: tMembership("freePlanLimitHeader", { defaultValue: defaultTranslations.freePlanLimitHeader }),
    freePlanLimitDescription1: tMembership("freePlanLimitDescription1", {
      defaultValue: defaultTranslations.freePlanLimitDescription1,
    }),
    freePlanLimitDescription2: tMembership("freePlanLimitDescription2", {
      defaultValue: defaultTranslations.freePlanLimitDescription2,
    }),
    enterBillingInfo: tMembership("enterBillingInfo", { defaultValue: defaultTranslations.enterBillingInfo }),
  }
}
